/**
 * IosNotificationGuideComponent — modal d'aide visuel ouvert via MatDialog
 * quand `Notification.permission === 'denied'` sur iOS PWA.
 *
 * Pourquoi ce composant existe :
 *   Safari iOS (et iOS PWA) NE redemande JAMAIS la permission native une
 *   fois qu'elle a été refusée. `Notification.requestPermission()` renvoie
 *   immédiatement `denied` sans prompt visible. La SEULE façon de
 *   réactiver les notifications est de passer par :
 *
 *     Réglages iOS → [Nom de l'app installée] → Notifications →
 *     Autoriser les notifications.
 *
 *   Le bouton "Ouvrir Réglages" tente l'URL scheme `app-settings:`,
 *   qui fonctionne dans une vraie app native iOS mais est généralement
 *   bloqué pour les PWA WebKit. On le propose quand même au cas où une
 *   future version d'iOS l'autorise — sinon les étapes textuelles
 *   numérotées guident l'utilisateur.
 */
import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { VdButtonComponent, VdDialogComponent } from '../../ui';

export interface IosNotifGuideData {
  appName?: string;
  // Si vrai, on adapte le wording pour le cas "permission denied"
  // (vs "iOS Safari hors PWA : installez d'abord").
  isDenied?: boolean;
  // Indique que l'app n'est pas installée en PWA — wording différent.
  requiresInstall?: boolean;
}

@Component({
  selector: 'app-ios-notification-guide',
  standalone: true,
  imports: [CommonModule, MatDialogModule, VdDialogComponent, VdButtonComponent],
  templateUrl: './ios-notification-guide.component.html',
  styleUrls: ['./ios-notification-guide.component.css'],
})
export class IosNotificationGuideComponent {
  readonly appName: string;
  readonly isDenied: boolean;
  readonly requiresInstall: boolean;

  constructor(
    public dialogRef: MatDialogRef<IosNotificationGuideComponent>,
    @Inject(MAT_DIALOG_DATA) data: IosNotifGuideData | null,
  ) {
    this.appName = data?.appName || 'Vendeur';
    this.isDenied = !!data?.isDenied;
    this.requiresInstall = !!data?.requiresInstall;
  }

  /**
   * Tente d'ouvrir directement les Réglages iOS via l'URL scheme natif.
   * Ne fonctionne pas dans la plupart des cas (Apple bloque ce schéma
   * depuis les PWA WebKit) — on garde quand même la tentative comme
   * shortcut au cas où.
   */
  tryOpenSettings(): void {
    try {
      window.location.href = 'app-settings:';
    } catch {
      /* ignore */
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
